/**
 * A whole-cut strip for the desk's Timeline and ASMR rows (Dax, 2026-09-29): the cut end to end, each
 * piece of sound drawn where it plays. For the timeline, the clips' own sound in sequence with a faint
 * mark at each cut; for ASMR, every clip's effect where it was pushed, overlapping where tails run on.
 * Pressing inside a clip's own span grabs that clip's piece, whatever overlaps it, and dragging moves it
 * as the card does (a slip for the timeline, a nudge for ASMR); the card follows live, and the strip
 * follows the card. Drawn on canvas like every waveform.
 *
 * Where the caller allows it (ASMR, 2026-10-03), a press also selects the piece: its heard stretch is
 * shaded until another is pressed, its trimmed-off rest is drawn faint, and handles at its two ends drag
 * its trim. Nothing before the cut's first frame or after its last is drawn: it does not play.
 *
 * Each piece carries its clip's colours (clip-hues.ts, 2026-10-04): the clip's span on the cut takes its
 * very pale background, its waveform its hue, drawn slightly translucent so tails running into a
 * neighbour's span blend with it. The selected piece alone turns to the blue shade with blue handles.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { ClipColours } from '@/lib/clip-hues';
import { displayGain } from './waveform';

/** How close (px) a press must land to a handle to take it rather than the piece. */
const HANDLE_HIT_PX = 6;
const HANDLE_W = 2;

/**
 * One piece of sound on the cut: its file's peaks spread evenly from `atMs` (where the file starts) for
 * `durationMs` (the whole file), of which `heardFromMs` to `heardToMs` plays; `off` draws it grey. `key`
 * names it to the caller; `holdFromMs` to `holdToMs` is where on the cut a press grabs it.
 */
export interface StripSegment { key: number; atMs: number; durationMs: number; peaks: number[]; heardFromMs: number; heardToMs: number; off: boolean; holdFromMs: number; holdToMs: number; colours?: ClipColours | undefined }

/** How opaque each piece's bars are, so overlapping tails visibly mix their colours. */
const BAR_ALPHA = 0.82;

export type TrimEdge = 'start' | 'end';

function cssVar(el: HTMLElement, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

export function CutStrip({ segments, totalMs, cuts, label, onDrag, selected = null, onSelect, onTrim }: {
  segments: StripSegment[];
  totalMs: number;
  /** Where one clip gives way to the next, on the cut's timeline. */
  cuts: number[];
  label: string;
  /** A grabbed piece dragged by `deltaMs` of the cut (right is later); `done` on release. */
  onDrag: (key: number, deltaMs: number, done: boolean) => void;
  /** The selected piece: shaded, with its handles when trimming is allowed. */
  selected?: number | null;
  onSelect?: (key: number) => void;
  /** A handle of the selected piece dragged by `deltaMs` (right is later); `done` on release. */
  onTrim?: (key: number, edge: TrimEdge, deltaMs: number, done: boolean) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ key: number; x: number; edge: TrimEdge | null } | null>(null);
  /** Where the pointer was last seen while dragging: where a drag whose release was lost ends. */
  const lastX = useRef(0);
  const draw = useCallback(() => {
    const canvas = ref.current;
    if (!canvas || totalMs <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx || !w || !h) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const on = cssVar(canvas, '--wf-sfx', '#a85a1f');
    const off = cssVar(canvas, '--wf-bar', '#d5d5da');
    const ghost = cssVar(canvas, '--wf-ghost', 'rgba(0,0,0,0.13)');
    const cut = cssVar(canvas, '--wf-cut', 'rgba(0,0,0,0.35)');
    const xAt = (ms: number) => (Math.max(0, Math.min(totalMs, ms)) / totalMs) * w;
    const chosen = segments.find((s) => s.key === selected) ?? null;
    // Each clip's span in its pale tint, then the selected piece's heard stretch in the blue shade.
    for (const s of segments) {
      if (!s.colours || s.key === selected) continue;
      ctx.fillStyle = s.colours.bg;
      const x0 = xAt(s.holdFromMs);
      ctx.fillRect(x0, 0, xAt(s.holdToMs) - x0, h);
    }
    if (chosen) {
      ctx.fillStyle = cssVar(canvas, '--wf-shade', 'rgba(10,111,224,0.1)');
      const x0 = xAt(chosen.atMs + chosen.heardFromMs);
      ctx.fillRect(x0, 0, xAt(chosen.atMs + chosen.heardToMs) - x0, h);
    }
    const barW = 3;
    const gap = 1;
    const bars = Math.floor(w / (barW + gap));
    /** The loudest of a piece's peaks between two moments of the file. */
    const peakIn = (s: StripSegment, gain: number, f0: number, f1: number) => {
      const i0 = Math.max(0, Math.floor((f0 / s.durationMs) * s.peaks.length));
      const i1 = Math.min(s.peaks.length, Math.max(i0 + 1, Math.ceil((f1 / s.durationMs) * s.peaks.length)));
      let p = 0;
      for (let i = i0; i < i1; i++) p = Math.max(p, s.peaks[i] ?? 0);
      return Math.min(1, p * gain);
    };
    // Each piece draws its own bars in its own colour; where tails overlap, the translucency mixes them.
    ctx.globalAlpha = BAR_ALPHA;
    for (const s of segments) {
      if (!s.peaks.length) continue;
      const gain = displayGain(s.peaks, 0, s.peaks.length);
      for (let b = 0; b < bars; b++) {
        const f0 = (b / bars) * totalMs - s.atMs;
        const f1 = ((b + 1) / bars) * totalMs - s.atMs;
        const inHeard = f1 > s.heardFromMs && f0 < s.heardToMs;
        const asGhost = !inHeard && s.key === selected && f1 > 0 && f0 < s.durationMs;
        if (!inHeard && !asGhost) continue;
        const p = inHeard ? peakIn(s, gain, Math.max(f0, s.heardFromMs), Math.min(f1, s.heardToMs)) : peakIn(s, gain, Math.max(0, f0), Math.min(s.durationMs, f1));
        if (p <= 0) continue;
        const bh = Math.max(2, p * (h - 4));
        ctx.fillStyle = asGhost ? ghost : s.off ? off : s.colours?.wave ?? on;
        ctx.fillRect(b * (barW + gap), (h - bh) / 2, barW, bh);
      }
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = cut;
    for (const at of cuts) if (at > 0 && at < totalMs) ctx.fillRect((at / totalMs) * w, 0, 1, h);
    if (chosen && onTrim) {
      ctx.fillStyle = cssVar(canvas, '--wf-handle', '#0a6fe0');
      for (const ms of [chosen.atMs + chosen.heardFromMs, chosen.atMs + chosen.heardToMs]) {
        const x = Math.max(0, Math.min(w - HANDLE_W, xAt(ms) - HANDLE_W / 2));
        ctx.fillRect(x, 0, HANDLE_W, h);
        ctx.fillRect(x - HANDLE_W, 0, HANDLE_W * 3, HANDLE_W * 3);
        ctx.fillRect(x - HANDLE_W, h - HANDLE_W * 3, HANDLE_W * 3, HANDLE_W * 3);
      }
    }
  }, [segments, totalMs, cuts, selected, onTrim]);

  useEffect(() => {
    draw();
    const canvas = ref.current;
    if (!canvas) return;
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  const msAt = (clientX: number) => {
    const rect = ref.current!.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * totalMs;
  };
  const deltaAt = (clientX: number) => ((clientX - dragRef.current!.x) / ref.current!.getBoundingClientRect().width) * totalMs;
  const grabbed = (clientX: number) => {
    const ms = msAt(clientX);
    return segments.find((s) => ms >= s.holdFromMs && ms < s.holdToMs) ?? null;
  };
  /** The selected piece's handle under the pointer, if trimming is allowed. */
  const handleAt = (clientX: number): TrimEdge | null => {
    const chosen = segments.find((s) => s.key === selected);
    if (!chosen || !onTrim) return null;
    const rect = ref.current!.getBoundingClientRect();
    const px = (ms: number) => rect.left + (Math.max(0, Math.min(totalMs, ms)) / totalMs) * rect.width;
    if (Math.abs(clientX - px(chosen.atMs + chosen.heardFromMs)) <= HANDLE_HIT_PX) return 'start';
    if (Math.abs(clientX - px(chosen.atMs + chosen.heardToMs)) <= HANDLE_HIT_PX) return 'end';
    return null;
  };
  /** A drag ends at the release, wherever it happens; one the strip never heard ends where it was last seen. */
  const finish = (clientX: number) => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = deltaAt(clientX);
    dragRef.current = null;
    if (drag.edge) onTrim?.(drag.key, drag.edge, delta, true);
    else onDrag(drag.key, delta, true);
  };
  return (
    <canvas ref={ref} className="sound-lane cut-strip" role="img" aria-label={label}
      onPointerDown={(e) => {
        const edge = handleAt(e.clientX);
        lastX.current = e.clientX;
        if (edge && selected !== null) {
          dragRef.current = { key: selected, x: e.clientX, edge };
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
        const hit = grabbed(e.clientX);
        if (!hit) return;
        onSelect?.(hit.key);
        dragRef.current = { key: hit.key, x: e.clientX, edge: null };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const drag = dragRef.current;
        // No button held: the release was lost (past the strip's edge, or the capture dropped); it ends here.
        if (drag && e.buttons === 0) return finish(lastX.current);
        if (drag) lastX.current = e.clientX;
        if (drag?.edge) onTrim?.(drag.key, drag.edge, deltaAt(e.clientX), false);
        else if (drag) onDrag(drag.key, deltaAt(e.clientX), false);
        else e.currentTarget.dataset['cursor'] = handleAt(e.clientX) ? 'trim' : grabbed(e.clientX) ? 'grab' : 'default';
      }}
      onPointerUp={(e) => finish(e.clientX)}
      onPointerCancel={() => finish(lastX.current)}
      onLostPointerCapture={() => finish(lastX.current)} />
  );
}
